// =============================================================================
// SIREPPAR - Consultas de partidos a la SEP (#388)
// =============================================================================
// Roles, etiquetas y tipos compartidos por las pantallas de consultas del partido
// y, después, por la consulta desde una intimación (#389).
// =============================================================================

/**
 * Roles del partido que usan las consultas. Espejo de Roles.EXTERNOS_CONSULTAS del
 * backend: CONSULTA_EXTERNA queda afuera. Si cambian allá, hay que cambiar acá.
 */
export const ROLES_CONSULTAS_PARTIDO = ['APODERADO_FIRMANTE', 'PRESIDENTE_FIRMANTE', 'VICEPRESIDENTE_FIRMANTE', 'CARGADOR'];

/**
 * Bandera de despliegue VITE_CONSULTAS_HABILITADAS, como la de lotes: en false no
 * hay pantallas, menú ni accesos de consultas, ni para el partido ni para la SEP.
 * Se pasa explícita en el build (deploy-frontend.sh y deploy-produccion.sh).
 */
export const CONSULTAS_HABILITADAS = import.meta.env.VITE_CONSULTAS_HABILITADAS === 'true';

export const puedeUsarConsultas = (rol?: string | null): boolean =>
  CONSULTAS_HABILITADAS &&
  !!rol && ROLES_CONSULTAS_PARTIDO.includes(rol);

export type EstadoConsulta = 'ENVIADA' | 'RESPONDIDA' | 'CERRADA';
export type OrigenMensaje = 'PARTIDO' | 'SEP';

export const ETIQUETA_ESTADO_CONSULTA: Record<EstadoConsulta, string> = {
  ENVIADA: 'Enviada',
  RESPONDIDA: 'Respondida',
  CERRADA: 'Cerrada',
};

export const CLASE_ESTADO_CONSULTA: Record<EstadoConsulta, string> = {
  ENVIADA: 'bg-blue-100 text-blue-800',
  RESPONDIDA: 'bg-green-100 text-green-800',
  CERRADA: 'bg-gray-100 text-gray-700',
};

export const ETIQUETA_ORIGEN: Record<OrigenMensaje, string> = {
  PARTIDO: 'Partido',
  SEP: 'SEP',
};

/** Límites del backend (#386). */
export const ASUNTO_MIN = 5;
export const ASUNTO_MAX = 200;
export const TEXTO_MAX = 10000;

/** Mensaje de error del backend, o uno por defecto. */
export function mensajeDeError(error: unknown, porDefecto: string): string {
  const e = error as { response?: { data?: { error?: { message?: string } | string; message?: string } } };
  const data = e?.response?.data;
  if (typeof data?.error === 'string') return data.error;
  return data?.error?.message || data?.message || porDefecto;
}

export function formatearFecha(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function formatearFechaHora(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.toLocaleDateString('es-AR')} ${d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })} hs`;
}

export function formatearTamanio(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// -----------------------------------------------------------------------------
// Circuito de la SEP (#390)
// -----------------------------------------------------------------------------

/**
 * Roles internos que responden, asignan y escriben notas internas. Espejo de
 * Roles.INTERNOS_ESCRITURA del backend: AUDITOR y MESA_ENTRADAS quedan en solo
 * lectura. Lista de roles PERMITIDOS a propósito: un rol nuevo no queda
 * habilitado por accidente.
 */
export const ROLES_ESCRITURA_CONSULTAS_SEP = ['ANALISTA', 'SUPERVISOR', 'AUTORIDAD_FIRMANTE', 'ADMINISTRADOR'];

export const puedeGestionarConsultas = (rol?: string | null): boolean =>
  !!rol && ROLES_ESCRITURA_CONSULTAS_SEP.includes(rol);
